import { useState, useEffect } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useOtp } from "context/OtpContext";
import MDTypography from "components/MDTypography";
import SignupModal from "components/custom/SignupModal";
import GhlSignupModal from "./GhlSignupModal";
import { useNavigate } from "react-router-dom";
import TermsModal from "./TermsModal";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDistanceToNow } from "date-fns";

const VALIDITY_PERIOD_TO_DAYS = Object.freeze({
  "1WEEK": 7,
  "2WEEK": 14,
  "3WEEK": 21,
  "1MONTH": 30,
  "3MONTH": 90,
  "6MONTH": 180,
  "1YEAR": 365,
  LIFETIME: null,
});

const formatDaysWindow = (days) => {
  if (days % 7 === 0) {
    const weeks = days / 7;
    return `${weeks} week${weeks === 1 ? "" : "s"}`;
  }

  return `${days} day${days === 1 ? "" : "s"}`;
};

// Styled Components
const HeroBanner = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "60vh", // Increased height for better impact
  minHeight: "600px",
  width: "100%",
  overflow: "hidden",
  backgroundColor: "#000", // Added for better image contrast
  [theme.breakpoints.down("sm")]: {
    height: "60vh",
    minHeight: "550px",
  },
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.3)", // Consistent overlay
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.1)",
    transition: "transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover img": {
    transform: "scale(1.15)",
  },
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    linear-gradient(
      180deg, 
      rgba(0,0,0,0.1) 0%,
      rgba(0,0,0,0.1) 40%,
      rgba(0,0,0,0.1) 80%,
      rgba(0,0,0,0.1) 100%
    )
  `,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.1) 100%)",
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "1200px",
  textAlign: "center",
  zIndex: 2,
}));

// New footer wrapper styled component
const FooterWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: "10px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "90%",
  maxWidth: "1200px",
  textAlign: "center",
  zIndex: 10,
  [theme.breakpoints.down("sm")]: {
    bottom: "15px", // Slightly closer to bottom on mobile
  },
}));

const RestaurantTitle = styled(Typography)(({ theme }) => ({
  color: "#fff",
  fontSize: "3rem",
  maxWidth: "850px",
  fontWeight: 800,
  marginBottom: theme.spacing(3),
  textShadow: "2px 4px 8px rgba(0,0,0,0.4)",
  letterSpacing: "0.5px",
  lineHeight: 1.1,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "120px",
    height: "3px",
    background: "linear-gradient(90deg, #238856, #2ab06f, #238856)",
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.8rem",
  },
}));

const LimitedOfferWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
  gap: "10px",
  marginBottom: "24px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
  },
}));

const LimitedOffer = styled(Typography)(({ theme }) => ({
  color: "rgb(130 92 15 / 1)",
  fontSize: "16px",
  background: "rgb(251 242 222 / 0.95)",
  padding: "6px 12px",
  borderRadius: "8px",
  fontWeight: 700,
  letterSpacing: "0.5px",
  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  display: "flex",
  alignItems: "center",
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const OfferText = styled(Typography)(({ theme }) => ({
  color: "#fff",
  fontSize: "18px",
  fontWeight: 500,
  textShadow: "1px 2px 4px rgba(0,0,0,0.3)",
  maxWidth: "800px",
  [theme.breakpoints.up("xl")]: {
    whiteSpace: "pre-line",
    wordWrap: "break-word",
    maxWidth: "100%",
  },
}));

// New error message component
const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: "#ffffff",
  fontSize: "0.9rem",
  fontWeight: 500,
  textAlign: "center",
  marginTop: "8px",
  background: "rgba(211, 47, 47, 0.85)",
  borderRadius: "4px",
  padding: "6px 12px",
  backdropFilter: "blur(4px)",
  maxWidth: "500px",
  margin: "8px auto 0",
  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  animation: "fadeIn 0.3s ease-in-out",
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(-5px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const GradientEmailInput = styled(TextField)(({ theme, error }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "40px",
    marginTop: 30,
    backgroundColor: "white",
    height: "56px",
    fontWeight: 500,
    position: "relative",
    "& fieldset": {
      borderColor: error ? "#d32f2f" : "#2ab06f",
      borderWidth: 2,
    },
    "&:hover fieldset": {
      borderColor: error ? "#d32f2f" : "#238856",
    },
    "&.Mui-focused fieldset": {
      borderColor: error ? "#d32f2f" : "#238856",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 20px",
    paddingRight: "130px", // Adjust value as needed based on button width
    fontSize: "1.1rem",
    height: "100%",
    boxSizing: "border-box",
  },
  "& .MuiInputAdornment-root": {
    marginLeft: 0,
    height: "calc(100% - 16px)",
    maxHeight: "none",
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
  },
}));

const StartNowButton = styled(Button)(({ theme }) => ({
  borderRadius: "40px",
  padding: "12px 24px",
  fontSize: "1rem",
  textTransform: "none",
  background: "linear-gradient(135deg, #238856 0%, #2ab06f 100%)",
  color: "#fff",
  fontWeight: 600,
  boxShadow: "0 4px 15px rgba(35, 136, 86, 0.2)",
  margin: 0,
  minHeight: "42px",
  whiteSpace: "nowrap",
  "&:hover": {
    background: "linear-gradient(135deg, #238856 0%, #2ab06f 100%)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.9rem",
  },
}));

const Banner = ({
  bannerImgStatus,
  bannerImgUrl,
  FoodImg,
  landingData,
  campaignDetails,
  setOpen,
  setAuthSection,
  campaignCode,
  gtmCode,
  referrerCode,
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const { identifier, isLoading, dispatch, message, isOtpSent } = useOtp();
  const [validationError, setValidationError] = useState("");
  const [displayError, setDisplayError] = useState("");
  const [sigupModalOpen, setSignupModalOpen] = useState(false);
  const [ghlSignupModalOpen, setGhlSignupModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [rewardMinimumOrderValue, setRewardMinimumOrderValue] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Effect to handle API error messages
  useEffect(() => {
    if (message?.type === "error") {
      setDisplayError(message.message);
    } else {
      setDisplayError("");
    }
  }, [message]);

  const handleSignupModalOpen = (e) => {
    e.preventDefault();

    const ghlStatus = landingData?.gohighlevelSyncEnabled;

    if (ghlStatus) {
      setGhlSignupModalOpen(true);

      return;
    }

    setSignupModalOpen(true);
  };

  const formatNumberUS = (value) => {
    if (typeof value !== "number") return value;
    return value.toLocaleString("en-US");
  };

  // Effect to handle response after OTP is sent
  useEffect(() => {
    if (!isLoading && identifier && !validationError) {
      if (message?.type === "error") {
        setDisplayError(message.message);
      } else if (isOtpSent) {
        // Only open the modal and change section if OTP was sent successfully
        setAuthSection("code");
        setOpen(true);
      }
    }
  }, [isLoading, message, isOtpSent]);

  useEffect(() => {
    const plans = landingData?.loyaltyPlan?.plans;
    if (!plans?.length) return;

    // Get minimumSale from the first Small detail (safe & fast)
    const firstSmallDetail = plans[0].details.find((d) => d.name === "Small");
    setRewardMinimumOrderValue(firstSmallDetail?.minimumSale ?? null);
  }, [landingData?.loyaltyPlan]);

  const calculateBaseCashback = () => {
    return Math.max(
      ...(landingData?.loyaltyPlan?.plans?.flatMap((plan) =>
        plan.details.map((detail) => detail.cashbackMultiple_100)
      ) || [0])
    );
  };

  const calculateCampaignMultiplier = () => {
    if (!campaignDetails?.campaign_campaign_details?.length) return 0;

    const multiplierDetail = campaignDetails.campaign_campaign_details.find(
      (detail) => detail.rewardType === "MULTIPLIER"
    );

    return multiplierDetail?.multiplierCashback || 0;
  };

  // New campaign override functions
  const formatBonusText = (detail) => {
    const { rewardType, fixedCashback, multiplierCashback } = detail;
    if (rewardType === "FIXED") {
      return `$${fixedCashback / 100 || 0}`;
    } else if (rewardType === "MULTIPLIER") {
      return `${multiplierCashback || 0}X`;
    }
    return "";
  };

  const formatConditionText = (detail) => {
    const { rewardCondition } = detail;
    if (rewardCondition === "FIRST_ORDER") {
      return "FIRST Order";
    } else if (rewardCondition === "ALL_ORDERS") {
      return "EVERY Order";
    }
    return "";
  };

  const getCombinedBonus = (campaignDetails, eligibleOrdersCount) => {
    if (!campaignDetails || campaignDetails.length === 0) {
      return { mainText: "No Bonus", subText: "" };
    }

    if (campaignDetails.length === 1) {
      const detail = campaignDetails[0];
      return {
        mainText: formatBonusText(detail),
        subText: eligibleOrdersCount
          ? `${eligibleOrdersCount} Orders`
          : formatConditionText(detail),
      };
    }

    if (campaignDetails.length === 2) {
      const firstOrder = campaignDetails.find((d) => d.rewardCondition === "FIRST_ORDER");
      const allOrders = campaignDetails.find((d) => d.rewardCondition === "ALL_ORDERS");

      if (firstOrder && allOrders) {
        const firstBonus = formatBonusText(firstOrder);
        const allOrdersBonus = formatBonusText(allOrders);
        return {
          mainText: `${allOrdersBonus} + ${firstBonus}`,
          subText: eligibleOrdersCount ? `${eligibleOrdersCount} Orders` : "EVERY Order",
        };
      } else {
        return {
          mainText: campaignDetails.map(formatBonusText).join(" + "),
          subText: campaignDetails.map(formatConditionText).join(" + "),
        };
      }
    }

    return { mainText: "Multiple Bonuses", subText: "" };
  };

  const getCampaignTitle = () => {
    const plans = landingData?.loyaltyPlan?.plans || [];
    const level1 = plans.find((p) => p.name === "Level 1");
    const level1Details = level1?.details || [];

    //  No loyalty plan at all → generic message
    if (!plans.length && (!campaignDetails || !campaignDetails.override)) {
      return "Join our rewards program and earn cashback on every order.";
    }

    //  1️⃣ No campaign || 2️⃣ Campaign exists & override false
    if (!campaignDetails || !campaignDetails.override) {
      if (plans.length === 1 && level1Details.length === 1) {
        return `Earn ${
          calculateBaseCashback() + calculateCampaignMultiplier()
        }X cashback points on EVERY order.`;
      }
      return `Earn up to ${
        calculateBaseCashback() + calculateCampaignMultiplier()
      }X cashback points on EVERY order.`;
    }

    const bonusInfo = getCombinedBonus(
      campaignDetails.campaign_campaign_details,
      campaignDetails.eligibleOrdersCount
    );

    const details = campaignDetails.campaign_campaign_details || [];

    if (details.length === 1) {
      const detail = details[0];
      if (detail.rewardType === "FIXED") {
        return `Earn ${bonusInfo.mainText} bonus rewards on your ${bonusInfo.subText}.`;
      } else if (detail.rewardType === "MULTIPLIER") {
        return `Earn ${bonusInfo.mainText} cashback points on ${bonusInfo.subText}.`;
      }
    }

    if (details.length === 2) {
      const allOrdersMultiplier = details.find(
        (d) => d.rewardCondition === "ALL_ORDERS" && d.rewardType === "MULTIPLIER"
      );
      if (allOrdersMultiplier) {
        return `Earn ${allOrdersMultiplier.multiplierCashback}X cashback points on EVERY order.`;
      }
    }

    // Fallback for multiple or unknown combinations
    return `Earn ${bonusInfo.mainText} on your orders.`;
  };

  const getCampaignSubtitle = () => {
    if (!campaignDetails || !campaignDetails.override) {
      return null;
    }

    const details = campaignDetails.campaign_campaign_details || [];

    // If only one reward (FIXED or MULTIPLIER), no subtitle
    if (details.length === 1) {
      return null;
    }

    // If more than one reward, show the FIXED reward if available
    const fixedReward = details.find((d) => d.rewardType === "FIXED");

    if (fixedReward) {
      const validity = formatValidityWindow(campaignDetails, { rewardCondition: "FIRST_ORDER" });

      const minSubtotal =
        campaignDetails?.minimumOrderValueInCents > 0
          ? `, with a minimum subtotal of $${(
              campaignDetails.minimumOrderValueInCents / 100
            ).toFixed(2)}.`
          : rewardMinimumOrderValue
          ? `, with a minimum subtotal of $${rewardMinimumOrderValue.toFixed(2)}.`
          : "";

      const orderText =
        fixedReward.rewardCondition === "FIRST_ORDER" ? "FIRST order" : "EVERY order";

      const text = `Earn an additional ${formatBonusText(fixedReward)} (${formatNumberUS(
        fixedReward?.fixedCashback
      )} pts) on your ${orderText} placed ${validity}${minSubtotal}`;

      return text.replace(/(,\s*)with a/g, "$1\nwith a");
    }
    return null;
  };

  const renderCampaignText = () => {
    const details = campaignDetails.campaign_campaign_details || [];

    const fixedReward = details.find((d) => d.rewardType === "FIXED");
    const multiplierReward = details.find((d) => d.rewardType === "MULTIPLIER");

    // Use the appropriate validity period based on reward type
    let validity;
    if (fixedReward) {
      validity = formatValidityWindow(campaignDetails, { rewardCondition: "FIRST_ORDER" });
    } else if (multiplierReward) {
      validity = formatValidityWindow(campaignDetails, { rewardCondition: "ALL_ORDERS" });
    } else {
      validity = formatValidityWindow(campaignDetails);
    }

    const minSubtotal =
      campaignDetails?.minimumOrderValueInCents > 0
        ? `, with a minimum subtotal of $${(campaignDetails.minimumOrderValueInCents / 100).toFixed(
            2
          )}.`
        : rewardMinimumOrderValue
        ? `, with a minimum subtotal of $${rewardMinimumOrderValue.toFixed(2)}.`
        : "";

    if (fixedReward) {
      const orderText =
        fixedReward.rewardCondition === "FIRST_ORDER" ? "FIRST order" : "EVERY order";

      const text = `Earn an additional ${formatBonusText(fixedReward)} (${formatNumberUS(
        fixedReward?.fixedCashback
      )} pts) on your ${orderText} placed ${validity}${minSubtotal}`;
      return text.replace(/(,\s*)with a/g, "$1\nwith a");
    }

    if (multiplierReward) {
      const orderText =
        multiplierReward.rewardCondition === "FIRST_ORDER" ? "FIRST order" : "EVERY order";

      const text = `Earn an additional ${calculateCampaignMultiplier()}X on your ${orderText} placed ${validity}${minSubtotal}`;
      return text.replace(/(,\s*)with a/g, "$1\nwith a");
    }

    return null;
  };

  const formatDuration = (duration) => {
    return duration
      .replace(/^1/, "1 ")
      .replace(/^A/, "A ")
      .replace(/([A-Z]+)$/, (match) => " " + match.toLowerCase());
  };

  const formatValidityWindow = (
    campaign,
    { includePeriod = false, rewardCondition = null } = {}
  ) => {
    const suffix = includePeriod ? "." : "";

    // If specific reward condition is requested, use that
    let targetDetail = null;
    if (rewardCondition) {
      targetDetail = campaign?.campaign_campaign_details?.find(
        (detail) => detail.rewardCondition === rewardCondition
      );
    } else {
      // Default to FIRST_ORDER for backward compatibility
      targetDetail = campaign?.campaign_campaign_details?.find(
        (detail) => detail.rewardCondition === "FIRST_ORDER"
      );
    }

    const validityDays = targetDetail?.validityPeriodInDays;

    if (typeof validityDays === "number" && !Number.isNaN(validityDays)) {
      if (validityDays <= 0) {
        return `after sign up${suffix}`;
      }

      return `within ${formatDaysWindow(validityDays)} after sign up${suffix}`;
    }

    return `after sign up${suffix}`;
  };

  const getCampaignTermsData = (campaignDetails) => {
    const plans = landingData?.loyaltyPlan?.plans || [];
    const level1 = plans.find((p) => p.name === "Level 1");
    const level1Details = level1?.details || [];

    // Fallback if no campaign
    if (!campaignDetails) {
      if (!plans.length) {
        return {
          title: "Join our rewards program and earn cashback on every order.",
          period: "ONGOING",
          points: [
            "Orders that are cancelled, refunded, or not completed will not qualify for rewards.",
            "Rewards calculated on meal subtotal redeemed as an Amazon.com Gift Card. T&Cs apply.",
          ],
        };
      }
      if (plans.length === 1 && level1Details.length === 1) {
        return {
          title: `Earn ${calculateBaseCashback()}X cashback points on EVERY order.`,
          period: "ONGOING",
          points: [
            "Orders that are cancelled, refunded, or not completed will not qualify for rewards.",
            "Rewards calculated on meal subtotal redeemed as an Amazon.com Gift Card. T&Cs apply.",
          ],
        };
      }
      return {
        title: `Earn up to ${
          calculateBaseCashback() + calculateCampaignMultiplier()
        }X cashback points on EVERY order.`,
        period: "ONGOING",
        points: [
          "Orders that are cancelled, refunded, or not completed will not qualify for rewards.",
          "Rewards calculated on meal subtotal redeemed as an Amazon.com Gift Card. T&Cs apply.",
        ],
      };
    }

    const {
      campaign_campaign_details: details,
      override,
      minimumOrderValueInCents,
      campaignExpiration,
      campaign_campaign_customers,
    } = campaignDetails;

    // Make sure expiration is valid
    let expirationDate = null;
    if (campaignExpiration) {
      const tempDate = new Date(campaignExpiration);
      if (!isNaN(tempDate)) expirationDate = tempDate;
    }

    const period = expirationDate
      ? `OFFER ENDS ${formatDistanceToNow(expirationDate, { addSuffix: true }).toUpperCase()}`
      : "ONGOING";

    const dynamicPoints = [];
    const earnFragments = [];

    // Fixed reward
    const fixed = details?.find((d) => d.rewardType === "FIXED");
    if (fixed && fixed.rewardCondition === "FIRST_ORDER") {
      const fixedValidity = formatValidityWindow(campaignDetails, {
        includePeriod: true,
        rewardCondition: "FIRST_ORDER",
      });
      dynamicPoints.push(`1st order must be placed ${fixedValidity}`);
      earnFragments.push(`$${fixed.fixedCashback / 100} on FIRST order`);
    }

    // Multiplier reward
    const multiplier = details?.find((d) => d.rewardType === "MULTIPLIER");
    if (multiplier?.multiplierCashback > 0) {
      const multiplierValidity = formatValidityWindow(campaignDetails, {
        includePeriod: true,
        rewardCondition: "ALL_ORDERS",
      });
      dynamicPoints.push(
        `Earn ${multiplier.multiplierCashback}X on EVERY fulfilled order ${multiplierValidity}`
      );
      earnFragments.push(`${multiplier.multiplierCashback}X points with EVERY placed order`);
    }

    // Override false logic
    if (!override) {
      if (plans.length === 1 && level1Details.length === 1) {
        dynamicPoints.push(
          `Earn ${calculateBaseCashback()}X on EVERY fulfilled order after sign up.`
        );
        earnFragments.push(`${calculateBaseCashback()}X points with EVERY placed order`);
      } else {
        dynamicPoints.push(
          `Earn up to ${
            calculateBaseCashback() + calculateCampaignMultiplier()
          }X on EVERY fulfilled order after sign up.`
        );
        earnFragments.push(
          `up to ${
            calculateBaseCashback() + calculateCampaignMultiplier()
          }X points with EVERY placed order`
        );
      }
    }

    let campaignTitle = "";
    if (multiplier?.multiplierCashback > 0 && !override) {
      const base = calculateBaseCashback();
      const total = base + multiplier.multiplierCashback;

      // Reset and rewrite the first two points
      dynamicPoints.length = 0;
      const multiplierValidity = formatValidityWindow(campaignDetails, {
        includePeriod: true,
        rewardCondition: "ALL_ORDERS",
      });
      dynamicPoints.push(
        `Earn an additional ${multiplier.multiplierCashback}X on EVERY fulfilled order ${multiplierValidity}`
      );

      if (plans.length === 1 && level1Details.length === 1) {
        campaignTitle = `Earn ${total}X points with EVERY placed order.`;
        dynamicPoints.push(`Earn ${base}X on EVERY fulfilled order after sign up.`);
      } else {
        campaignTitle = `Earn up to ${total}X points with EVERY placed order.`;
        dynamicPoints.push(`Earn up to ${base}X on EVERY fulfilled order after sign up.`);
      }
    } else if (earnFragments.length === 1) campaignTitle = `Earn ${earnFragments[0]}.`;
    else if (earnFragments.length > 1) campaignTitle = `Earn ${earnFragments.join(" and ")}.`;

    if (campaign_campaign_customers && campaign_campaign_customers.length > 0) {
      dynamicPoints.push("Offer is valid for selected customers only and cannot be transferred.");
    }
    // Minimum order value
    const minValue =
      minimumOrderValueInCents > 0
        ? minimumOrderValueInCents / 100
        : rewardMinimumOrderValue ?? null;
    if (minValue) {
      dynamicPoints.push(
        `Minimum order value (subtotal) must be $${minValue.toFixed(
          2
        )}. (excluding taxes, fees, and delivery charges).`
      );
    }

    // Campaign caps — combine usage limit and per-order cashback cap into one sentence
    const capParts = [];
    if (campaignDetails.eligibleOrdersCount > 0) {
      const scopeQualifier =
        campaignDetails.scope === "BRAND"
          ? " across all stores under this brand"
          : "";
      capParts.push(
        `Valid for up to ${campaignDetails.eligibleOrdersCount} catering order${
          campaignDetails.eligibleOrdersCount === 1 ? "" : "s"
        }${scopeQualifier}`
      );
    }
    if (campaignDetails.maxCashbackInCents > 0) {
      const cashbackFragment = `up to $${(
        campaignDetails.maxCashbackInCents / 100
      ).toFixed(2)} cashback per order`;
      capParts.push(
        capParts.length === 0 ? `Earn ${cashbackFragment}` : `with ${cashbackFragment}`
      );
    }
    if (capParts.length > 0) {
      dynamicPoints.push(`${capParts.join(", ")}.`);
    }

    // Restaurant link — only show when restaurantPrefix is available (store pages)
    const restaurantPrefix = landingData?.restaurantPrefix;
    if (restaurantPrefix) {
      const restaurantUrl = `/restaurants/${restaurantPrefix}`;

      if (plans.length === 1 && level1Details.length === 1) {
        dynamicPoints.push(
          <span key="restaurant-link-1">
            When offer ends, you will still earn {calculateBaseCashback()}X reward from the{" "}
            <a
              href={restaurantUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline", color: "#238856" }}
            >
              restaurant
            </a>
            .
          </span>
        );
      } else {
        dynamicPoints.push(
          <span key="restaurant-link-2">
            When offer ends, you will still earn up to {calculateBaseCashback()}X reward from the{" "}
            <a
              href={restaurantUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline", color: "#238856" }}
            >
              restaurant
            </a>
            .
          </span>
        );
      }
    }

    const fixedPoints = [
      "Orders that are cancelled, refunded, or not completed will not qualify for rewards.",
      "The campaign may be modified or terminated at any time without prior notice.",
      "Rewards are valid only for catering menu orders placed through the catering website. Rewards are calculated on the meal subtotal and redeemed as an Amazon.com Gift Card. T&Cs apply.",
    ];

    return {
      title: campaignTitle,
      period,
      points: [...dynamicPoints, ...fixedPoints],
    };
  };

  const termsData = getCampaignTermsData(campaignDetails);

  // Get the error message to display
  const errorToDisplay = displayError || validationError;

  return (
    <>
      <HeroBanner>
        <BannerImage>
          <img
            src={
              bannerImgStatus === 2
                ? ""
                : bannerImgStatus === 1 && bannerImgUrl
                ? bannerImgUrl
                : FoodImg
            }
            alt={landingData?.name}
          />
        </BannerImage>

        <Overlay />

        <ContentWrapper sx={{ opacity: isVisible ? 1 : 0 }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <RestaurantTitle variant="h1">{getCampaignTitle()}</RestaurantTitle>
          </div>

          {!campaignDetails &&
            landingData?.loyaltyPlan?.plans?.length === 1 &&
            (() => {
              const plan = landingData.loyaltyPlan.plans[0];
              const planDetail = plan.details?.find((detail) => detail.name === "Small");
              if (!planDetail) return null;

              return (
                <LimitedOfferWrapper>
                  <OfferText>
                    A minimum order value (sub-total) of ${planDetail.minimumSale} is required.
                  </OfferText>
                </LimitedOfferWrapper>
              );
            })()}

          {campaignDetails && !campaignDetails.override && renderCampaignText() && (
            <LimitedOfferWrapper>
              <LimitedOffer>LIMITED TIME OFFER</LimitedOffer>
              <OfferText>{renderCampaignText()}*</OfferText>
            </LimitedOfferWrapper>
          )}

          {campaignDetails && campaignDetails.override && getCampaignSubtitle() && (
            <LimitedOfferWrapper>
              <LimitedOffer>LIMITED TIME OFFER</LimitedOffer>
              <OfferText>{getCampaignSubtitle()}*</OfferText>
            </LimitedOfferWrapper>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              maxWidth: 600,
              margin: "auto",
              padding: 2,
            }}
          >
            <StartNowButton
              onClick={handleSignupModalOpen}
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Get Started "}
            </StartNowButton>
            {errorToDisplay && <ErrorMessage>{errorToDisplay}</ErrorMessage>}
            <SignupModal
              isOpen={sigupModalOpen}
              onClose={() => setSignupModalOpen(false)}
              gtmCode={gtmCode}
              campaignCode={campaignCode}
              referrerCode={referrerCode}
              campaignDetails={campaignDetails}
              landingData={landingData}
            />
            <GhlSignupModal
              isOpen={ghlSignupModalOpen}
              onClose={() => setGhlSignupModalOpen(false)}
              gtmCode={gtmCode}
              campaignCode={campaignCode}
              landingData={landingData}
              referrerCode={referrerCode}
              campaignDetails={campaignDetails}
            />
          </Box>
        </ContentWrapper>

        {/* Footer text moved outside ContentWrapper to a dedicated FooterWrapper */}
        <FooterWrapper>
          {!!campaignDetails ? (
            <MDTypography
              sx={{
                fontSize: "0.75rem",
                color: "white !important",
                fontWeight: 500,
                lineHeight: 1.6,
                cursor: "pointer",
              }}
              onClick={() => setTermsModalOpen(true)}
            >
              Offer Info <ExpandMoreIcon sx={{ fontSize: "1rem", color: "white !important" }} />
            </MDTypography>
          ) : (
            <MDTypography
              sx={{
                fontSize: "0.75rem",
                color: "white !important",
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              *Rewards are valid only for catering menu orders placed through the catering website.
              Rewards are calculated based on the meal subtotal and redeemed as Amazon.com Gift
              Cards. T&Cs apply.
            </MDTypography>
          )}
        </FooterWrapper>
      </HeroBanner>
      <TermsModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        campaignTitle={termsData.title}
        campaignPeriod={termsData.period}
        terms={termsData.points}
        isCampaign={!!campaignDetails}
        setSignupModalOpen={setSignupModalOpen}
      />
    </>
  );
};

Banner.propTypes = {
  bannerImgStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
  bannerImgUrl: PropTypes.string,
  campaignDetails: PropTypes.any,
  campaignCode: PropTypes.string,
  gtmCode: PropTypes.string,
  FoodImg: PropTypes.string.isRequired,
  referrerCode: PropTypes.string,
  landingData: PropTypes.shape({
    name: PropTypes.string,
    gohighlevelSyncEnabled: PropTypes.bool,
    restaurantPrefix: PropTypes.string,
    loyaltyPlan: PropTypes.shape({
      plans: PropTypes.arrayOf(
        PropTypes.shape({
          details: PropTypes.arrayOf(
            PropTypes.shape({
              cashbackMultiple_100: PropTypes.number,
              minimumSale: PropTypes.number,
            })
          ),
        })
      ),
    }),
  }),
  setOpen: PropTypes.any,
  setAuthSection: PropTypes.any,
};

export default Banner;
